import { ExternalLink, FileText, Trash2 } from 'lucide-react';
import * as React from 'react';

import { formatFileSize } from '@/components/documents-v2/utils/fileUtils';
import { Button } from '@/components/ui/button';
import { ReadPostgridAttachment } from '@/services/api/generated/postgridService';

// Extended type to include artifact_id until API types are regenerated
interface ExtendedReadPostgridAttachment extends ReadPostgridAttachment {
  artifact_id?: string;
}

interface AttachmentItemProps {
  attachment: ReadPostgridAttachment;
  onRemove?: (id: string) => void;
  matterId?: string;
}

export const AttachmentItem: React.FC<AttachmentItemProps> = ({
  attachment,
  onRemove,
  matterId,
}) => {
  // Cast to extended type to access artifact_id until API types are updated
  const extendedAttachment = attachment as ExtendedReadPostgridAttachment;
  // Handle missing blob data
  if (!attachment.blob) {
    return (
      <div className="group flex items-center justify-between p-3 border border-slate-100 rounded-lg bg-slate-50">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-500 truncate">
              Missing file data
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Attachment ID: {attachment.id}
            </div>
            {attachment.description && (
              <p
                className="text-xs text-slate-500 mt-1 truncate"
                title={attachment.description}
              >
                {attachment.description}
              </p>
            )}
          </div>
        </div>
        {onRemove && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-slate-500 hover:text-red-600"
              onClick={() => onRemove(attachment.id)}
              title="Remove attachment"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  const handleDownload = () => {
    // Construct download URL from blob ID - this matches the pattern from the backend
    const downloadUrl = `/api/blobs/${attachment.blob.id}/download/`;
    window.open(downloadUrl, '_blank');
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove(attachment.id);
    }
  };

  const handleViewArtifact = () => {
    if (extendedAttachment.artifact_id && matterId) {
      const artifactUrl = `/matters/${matterId}/artifacts/${extendedAttachment.artifact_id}`;
      window.open(artifactUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Determine primary action based on whether artifact_id exists
  const hasPrimaryAction = extendedAttachment.artifact_id && matterId;
  const primaryAction = hasPrimaryAction ? handleViewArtifact : handleDownload;
  const primaryHoverClass = hasPrimaryAction
    ? 'hover:border-slate-300 hover:bg-slate-50'
    : 'hover:border-slate-300';

  return (
    <div
      className={`group flex items-center justify-between p-3 border border-slate-100 rounded-lg transition-colors cursor-pointer ${primaryHoverClass}`}
      onClick={primaryAction}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          primaryAction();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={
        hasPrimaryAction
          ? `View artifact details for ${attachment.blob.name}`
          : `Download ${attachment.blob.name}`
      }
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div
            className={`text-sm truncate block text-left ${hasPrimaryAction ? 'text-slate-700 group-hover:text-slate-900' : 'text-slate-700 group-hover:text-slate-900'}`}
            title={attachment.blob.name}
          >
            {attachment.blob.name}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
            <span>{formatFileSize(attachment.blob.file_size)}</span>
            {attachment.blob.file_extension && (
              <>
                <span>•</span>
                <span>{attachment.blob.file_extension.toUpperCase()}</span>
              </>
            )}
          </div>
          {attachment.description && (
            <p
              className="text-xs text-slate-500 mt-1 truncate"
              title={attachment.description}
            >
              {attachment.description}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Show download button - always available */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700"
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering the main click
            handleDownload();
          }}
          title="Download file"
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-500 hover:text-red-600"
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the main click
              handleRemove();
            }}
            title="Remove attachment"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
